import { Field, FormLayout, HookForm } from '@/components_v2/HookForm';
import { useModals } from '@/hooks/ModalManager';
import { useCreateAutomationGroup } from '@/screens/Tickets/hooks/useCreateAutomationGroup';
import { AutomationGroupItem } from '@/screens/Tickets/hooks/useFetchAutomationGroups';
import { useUpdateAutomationGroup } from '@/screens/Tickets/hooks/useUpdateAutomationGroup';
import { Box, Button, ButtonGroup, Flex } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

const getGroupFormSchema = (t: any) => yup
  .object({
    name: yup
      .string()
      .min(2, t('validations.min_two_chars'))
      .required(t('validations.name_required')),
  })
  .required();

type GroupFormSchema = yup.InferType<typeof groupFormSchema>;

const WrappedForm = (props: { groupItem?: AutomationGroupItem }) => {
  const { groupItem } = props;
  const { t } = useTranslation();
  const groupFormSchema = getGroupFormSchema(t);
  const modals = useModals();
  const { createAutoGroup, loading } = useCreateAutomationGroup();
  const {
    updateAutomationGroup,
    loading: updating,
  } = useUpdateAutomationGroup();

  const onClose = React.useCallback(
    () => modals.close('create-automation-group-modal'),
    [modals],
  );

  const handleSubmit = React.useCallback(
    async (values: GroupFormSchema) => {
      let hasError = false;
      if (groupItem?.id) {
        const res = await updateAutomationGroup({
          id: groupItem?.id,
          name: values.name,
        });
        hasError = !!res.errors?.length;
      } else {
        const res = await createAutoGroup({
          name: values.name,
        });
        hasError = !!res.errors?.length;
      }

      if (!hasError) {
        onClose();
      }
    },
    [createAutoGroup, groupItem?.id, onClose, updateAutomationGroup],
  );

  const isLoading = updating || loading;

  return (
    <fieldset disabled={isLoading}>
      <HookForm<GroupFormSchema>
        px="6"
        pb="4"
        autoComplete="off"
        id="step-form"
        resolver={yupResolver(groupFormSchema)}
        schema={groupFormSchema}
        defaultValues={{
          name: groupItem?.name || '',
        }}
        onSubmit={handleSubmit}
      >
        <FormLayout>
          <Field<GroupFormSchema> name="name" label={t('form_labels.label')} />
        </FormLayout>

        <Flex justifyContent="flex-end">
          <ButtonGroup mt="4">
            <Button
              isDisabled={isLoading}
              variant="outline"
              onClick={() => {
                modals.close('create-automation-group-modal');
              }}
            >
              {t('buttons.cancel')}
            </Button>
            <Button isLoading={isLoading} type="submit" colorScheme="primary">
              {groupItem?.id ? t('common.edit') : t('common.create')}
            </Button>
          </ButtonGroup>
        </Flex>
      </HookForm>
    </fieldset>
  );
};

export function useCreateFormAutomationGroupModal() {
  const modals = useModals();
  const { t } = useTranslation();

  const openForm = React.useCallback(
    (groupItem?: AutomationGroupItem) => {
      modals.open({
        id: 'create-automation-group-modal',
        title: groupItem?.id
          ? t('modal_titles.edit_automation_group')
          : t('modal_titles.create_automation_group'),
        body: (
          <Box flex="1">
            <WrappedForm groupItem={groupItem} />
          </Box>
        ),
      });
    },
    [modals],
  );

  return { openForm };
}
